import React, { useState, useMemo } from 'react';
import {
  Button,
  Input,
  Checkbox,
  DatePicker,
  Select,
  Space,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  DownloadOutlined,
  SyncOutlined,
  FilterOutlined,
  SearchOutlined,
  UpOutlined,
  DownOutlined,
  EyeOutlined,
  SendOutlined,
  EditOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './VCCSearch.scss';

const { RangePicker } = DatePicker;
const { Option } = Select;

// Reusable Filter Section Component
const FilterSection = ({ title, children }) => {
  return (
    <div className="filter-section">
      <h4 className="filter-section-title">{title}</h4>
      <div className="filter-section-content">{children}</div>
    </div>
  );
};

// Reusable Input Group Component
const InputGroup = ({ children }) => {
  return <div className="input-group">{children}</div>;
};

// Reusable Status Badge Component
const StatusBadge = ({ status }) => {
  const getStatusConfig = (status) => {
    const configs = {
      Charged: { color: '#52c41a', bg: '#f6ffed' },
      'OTP Sent': { color: '#1890ff', bg: '#e6f7ff' },
      Failed: { color: '#ff4d4f', bg: '#fff1f0' },
      Issued: { color: '#1890ff', bg: '#e6f7ff' },
      Expired: { color: '#8c8c8c', bg: '#f5f5f5' },
    };
    return configs[status] || { color: '#8c8c8c', bg: '#f5f5f5' };
  };

  const config = getStatusConfig(status);

  return (
    <span
      className="status-badge"
      style={{
        backgroundColor: config.bg,
        color: config.color,
      }}
    >
      {status}
    </span>
  );
};

// Masked Card Number Component
const MaskedCardNumber = ({ number }) => {
  const parts = number.match(/.{1,4}/g) || [];
  return (
    <div className="masked-card">
      {parts.map((part, idx) => (
        <span key={idx} className="card-part">
          {part}
        </span>
      ))}
    </div>
  );
};

// Action Icons Component
const ActionIcons = () => {
  return (
    <div className="action-icons">
      <Button type="text" icon={<EyeOutlined />} size="small" />
      <Button type="text" icon={<SendOutlined />} size="small" />
      <Button type="text" icon={<EditOutlined />} size="small" />
      <Button type="text" icon={<ClockCircleOutlined />} size="small" />
    </div>
  );
};

// Main VCC Search Component
const VCCSearchManagement = () => {
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState({
    bookingId: '',
    pnr: '',
    guestName: '',
    hotelName: '',
    cardNumber: '',
    transactionId: '',
    purchaseId: '',
    dateType: 'Issue Date',
    quickRange: '',
    startDate: null,
    endDate: null,
    minAmount: '',
    maxAmount: '',
    statusFilters: {
      all: false,
      issued: false,
      charged: false,
      otpSent: false,
      notCharged: false,
      partiallyCharged: false,
      expired: false,
      failed: false,
    },
  });

  // Sample data
  const rowData = [
    {
      vccNumber: '5123********4567',
      bookingId: 'BK12847',
      guestName: 'Rajesh Kumar',
      hotelName: 'Taj Palace Mumbai',
      issueDate: '2025-11-28',
      validUntil: '2025-12-05',
      amount: 8450,
      status: 'Charged',
      chargeDate: '2025-11-30',
      remaining: 0,
    },
    {
      vccNumber: '5123********8901',
      bookingId: 'BK12846',
      guestName: 'Priya Sharma',
      hotelName: 'ITC Grand Goa',
      issueDate: '2025-11-29',
      validUntil: '2025-12-06',
      amount: 12300,
      status: 'OTP Sent',
      chargeDate: '-',
      remaining: 12300,
    },
    {
      vccNumber: '5123********2345',
      bookingId: 'BK12845',
      guestName: 'Amit Patel',
      hotelName: 'Oberoi Udaipur',
      issueDate: '2025-11-27',
      validUntil: '2025-12-04',
      amount: 15600,
      status: 'Failed',
      chargeDate: '-',
      remaining: 15600,
    },
    {
      vccNumber: '5123********6789',
      bookingId: 'BK12844',
      guestName: 'Sneha Reddy',
      hotelName: 'Leela Palace Bangalore',
      issueDate: '2025-11-28',
      validUntil: '2025-12-05',
      amount: 9800,
      status: 'Charged',
      chargeDate: '2025-11-29',
      remaining: 0,
    },
    {
      vccNumber: '5123********3456',
      bookingId: 'BK12843',
      guestName: 'Vikram Singh',
      hotelName: 'Hyatt Regency Delhi',
      issueDate: '2025-11-26',
      validUntil: '2025-12-03',
      amount: 7200,
      status: 'Issued',
      chargeDate: '-',
      remaining: 7200,
    },
    {
      vccNumber: '5123********7890',
      bookingId: 'BK12842',
      guestName: 'Anita Shah',
      hotelName: 'Marriott',
      issueDate: '2025-11-25',
      validUntil: '2025-12-02',
      amount: 6450,
      status: 'Expired',
      chargeDate: '-',
      remaining: 6450,
    },
  ];

  // Column Definitions for AG Grid
  const columnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 50,
        pinned: 'left',
      },
      {
        field: 'vccNumber',
        headerName: 'VCC Number',
        width: 180,
        pinned: 'left',
        cellRenderer: (params) => <MaskedCardNumber number={params.value} />,
      },
      {
        field: 'bookingId',
        headerName: 'Booking ID',
        width: 130,
        cellRenderer: (params) => (
          <a className="booking-link">{params.value}</a>
        ),
      },
      {
        field: 'guestName',
        headerName: 'Guest Name',
        width: 150,
      },
      {
        field: 'hotelName',
        headerName: 'Hotel Name',
        width: 180,
      },
      {
        field: 'issueDate',
        headerName: 'Issue Date',
        width: 120,
      },
      {
        field: 'validUntil',
        headerName: 'Valid Until',
        width: 120,
      },
      {
        field: 'amount',
        headerName: 'Amount',
        width: 120,
        cellRenderer: (params) => `₹${params.value.toLocaleString()}`,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        cellRenderer: (params) => <StatusBadge status={params.value} />,
      },
      {
        field: 'chargeDate',
        headerName: 'Charge Date',
        width: 120,
      },
      {
        field: 'remaining',
        headerName: 'Remaining',
        width: 120,
        cellRenderer: (params) =>
          params.value > 0 ? `₹${params.value.toLocaleString()}` : '₹0',
      },
      {
        headerName: 'Actions',
        width: 150,
        pinned: 'right',
        cellRenderer: () => <ActionIcons />,
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
    }),
    []
  );

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleStatusFilterChange = (status, checked) => {
    setFilters((prev) => ({
      ...prev,
      statusFilters: {
        ...prev.statusFilters,
        [status]: checked,
      },
    }));
  };

  const handleSearch = () => {
    console.log('Searching with filters:', filters);
  };

  const handleResetFilters = () => {
    setFilters({
      bookingId: '',
      pnr: '',
      guestName: '',
      hotelName: '',
      cardNumber: '',
      transactionId: '',
      purchaseId: '',
      dateType: 'Issue Date',
      quickRange: '',
      startDate: null,
      endDate: null,
      minAmount: '',
      maxAmount: '',
      statusFilters: {
        all: false,
        issued: false,
        charged: false,
        otpSent: false,
        notCharged: false,
        partiallyCharged: false,
        expired: false,
        failed: false,
      },
    });
  };

  return (
    <div className="vcc-search-container">
      {/* Header */}
      <div className="vcc-header">
        <h1 className="vcc-title">VCC Search & Management</h1>
        <div className="vcc-header-actions">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            className="btn-generate"
          >
            Generate New VCC
          </Button>
          <Button icon={<DownloadOutlined />} size="large">
            Export Results
          </Button>
          <Button
            type="primary"
            icon={<SyncOutlined />}
            size="large"
            className="btn-refresh"
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Advanced Filters */}
      <div className="filters-container">
        <div
          className="filters-header"
          onClick={() => setShowFilters(!showFilters)}
        >
          <div className="filters-header-left">
            <FilterOutlined />
            <span>Advanced Filters</span>
          </div>
          {showFilters ? <UpOutlined /> : <DownOutlined />}
        </div>

        {showFilters && (
          <div className="filters-content">
            {/* Booking Details */}
            <FilterSection title="Booking Details">
              <InputGroup>
                <Input
                  placeholder="Booking ID"
                  value={filters.bookingId}
                  onChange={(e) =>
                    handleFilterChange('bookingId', e.target.value)
                  }
                  size="large"
                />
                <Input
                  placeholder="PNR"
                  value={filters.pnr}
                  onChange={(e) => handleFilterChange('pnr', e.target.value)}
                  size="large"
                />
                <Input
                  placeholder="Guest Name"
                  value={filters.guestName}
                  onChange={(e) =>
                    handleFilterChange('guestName', e.target.value)
                  }
                  size="large"
                />
                <Input
                  placeholder="Hotel Name"
                  value={filters.hotelName}
                  onChange={(e) =>
                    handleFilterChange('hotelName', e.target.value)
                  }
                  size="large"
                />
              </InputGroup>
            </FilterSection>

            {/* VCC Details */}
            <FilterSection title="VCC Details">
              <InputGroup>
                <Input
                  placeholder="Card Number (partial)"
                  value={filters.cardNumber}
                  onChange={(e) =>
                    handleFilterChange('cardNumber', e.target.value)
                  }
                  size="large"
                />
                <Input
                  placeholder="Transaction ID"
                  value={filters.transactionId}
                  onChange={(e) =>
                    handleFilterChange('transactionId', e.target.value)
                  }
                  size="large"
                />
                <Input
                  placeholder="Purchase ID"
                  value={filters.purchaseId}
                  onChange={(e) =>
                    handleFilterChange('purchaseId', e.target.value)
                  }
                  size="large"
                />
              </InputGroup>
            </FilterSection>

            {/* Date Filters */}
            <FilterSection title="Date Filters">
              <InputGroup>
                <Select
                  placeholder="Date Type"
                  value={filters.dateType}
                  onChange={(value) => handleFilterChange('dateType', value)}
                  size="large"
                  style={{ width: '100%' }}
                >
                  <Option value="Issue Date">Issue Date</Option>
                  <Option value="Valid Until">Valid Until</Option>
                  <Option value="Charge Date">Charge Date</Option>
                </Select>
                <Select
                  placeholder="Quick Range"
                  value={filters.quickRange}
                  onChange={(value) => handleFilterChange('quickRange', value)}
                  size="large"
                  style={{ width: '100%' }}
                >
                  <Option value="today">Today</Option>
                  <Option value="yesterday">Yesterday</Option>
                  <Option value="last7days">Last 7 Days</Option>
                  <Option value="last30days">Last 30 Days</Option>
                  <Option value="thisMonth">This Month</Option>
                </Select>
                <DatePicker
                  placeholder="dd/mm/yyyy"
                  size="large"
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                />
                <DatePicker
                  placeholder="dd/mm/yyyy"
                  size="large"
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                />
              </InputGroup>
            </FilterSection>

            {/* Status and Amount Filters */}
            <div className="filters-row">
              <FilterSection title="Status Filters">
                <div className="checkbox-grid">
                  <Checkbox
                    checked={filters.statusFilters.all}
                    onChange={(e) =>
                      handleStatusFilterChange('all', e.target.checked)
                    }
                  >
                    All
                  </Checkbox>
                  <Checkbox
                    checked={filters.statusFilters.issued}
                    onChange={(e) =>
                      handleStatusFilterChange('issued', e.target.checked)
                    }
                  >
                    Issued
                  </Checkbox>
                  <Checkbox
                    checked={filters.statusFilters.otpSent}
                    onChange={(e) =>
                      handleStatusFilterChange('otpSent', e.target.checked)
                    }
                  >
                    OTP Sent
                  </Checkbox>
                  <Checkbox
                    checked={filters.statusFilters.charged}
                    onChange={(e) =>
                      handleStatusFilterChange('charged', e.target.checked)
                    }
                  >
                    Charged
                  </Checkbox>
                  <Checkbox
                    checked={filters.statusFilters.partiallyCharged}
                    onChange={(e) =>
                      handleStatusFilterChange(
                        'partiallyCharged',
                        e.target.checked
                      )
                    }
                  >
                    Partially Charged
                  </Checkbox>
                  <Checkbox
                    checked={filters.statusFilters.notCharged}
                    onChange={(e) =>
                      handleStatusFilterChange('notCharged', e.target.checked)
                    }
                  >
                    Not Charged
                  </Checkbox>
                  <Checkbox
                    checked={filters.statusFilters.expired}
                    onChange={(e) =>
                      handleStatusFilterChange('expired', e.target.checked)
                    }
                  >
                    Expired
                  </Checkbox>
                  <Checkbox
                    checked={filters.statusFilters.failed}
                    onChange={(e) =>
                      handleStatusFilterChange('failed', e.target.checked)
                    }
                  >
                    Failed
                  </Checkbox>
                </div>
              </FilterSection>

              <FilterSection title="Amount Range (INR)">
                <div className="amount-range">
                  <Input
                    placeholder="Min"
                    value={filters.minAmount}
                    onChange={(e) =>
                      handleFilterChange('minAmount', e.target.value)
                    }
                    size="large"
                  />
                  <Input
                    placeholder="Max"
                    value={filters.maxAmount}
                    onChange={(e) =>
                      handleFilterChange('maxAmount', e.target.value)
                    }
                    size="large"
                  />
                </div>
              </FilterSection>
            </div>

            {/* Filter Actions */}
            <div className="filter-actions">
              <Button
                type="primary"
                icon={<SearchOutlined />}
                size="large"
                onClick={handleSearch}
                className="btn-search"
              >
                Search
              </Button>
              <Button size="large" onClick={handleResetFilters}>
                Reset Filters
              </Button>
              <Button size="large">Save Filter Preset</Button>
            </div>
          </div>
        )}
      </div>

      {/* AG Grid Table */}
      <div className="vcc-table-container">
        <div className="ag-theme-alpine" style={{ height: '600px', width: '100%' }}>
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            animateRows={true}
            pagination={true}
            paginationPageSize={20}
          />
        </div>
      </div>
    </div>
  );
};

export default VCCSearchManagement;